import React, {useState} from 'react';
import {
    Typography, FormControlLabel, Switch, TextField, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {DataGrid} from "@mui/x-data-grid";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {frFR} from '@mui/x-data-grid/locales';
import {fr} from "date-fns/locale";
import {eachDayOfInterval, format, isValid} from "date-fns";
import Constants from "../../../utils/constants.js";
import {connect, useDispatch} from "react-redux";
import {heuresActions} from "../../../modules/heures/heures.actions.js";
import EquipageDetailModal from "./heures/EquipageDetailModal.js";
import FdtCreateModal from "./heures/FdtCreateModal.js";
import { PanierRepasIcon } from "../../../utils/ui/Icons/PanierRepasIcon.js";
import Grid from "@mui/material/Grid2";
import {FlightTakeoff} from "@mui/icons-material";


function RHSuiviHeuresTable({heures, dateRange, loading, onRefresh}) {
    const [modalOpen, setModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [filterNonValidated, setFilterNonValidated] = useState(false);
    const [searchOperateur, setSearchOperateur] = useState('');
    const dispatch = useDispatch();

    // Insensible à la casse et aux accents (« jerome » doit matcher « Jérôme »)
    const normalizeText = (s) => (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

    const handleCellClick = (params) => {
        const data = params.value;
        const rowData = params.row;

        if (data && rowData) {
            const operateur = rowData.operateur || (typeof rowData.name === 'string' ? rowData.name.split(" (")[0] : "");
            setSelectedData({
                ...data,
                operateur: operateur
            });
        }

        // Si c'est une cellule grise (aucune feuille de temps n'existe)
        if (data && (data.status === 'gris' || data.status === 'rouge')) {
            // Assurons-nous que nous avons l'ID de l'opérateur
            if (data.details && data.details.operateur && data.details.operateur.id) {
                setSelectedData(prevData => ({
                    ...prevData,
                    operateurId: data.details.operateur.id
                }));
                setCreateModalOpen(true);
            } else {
                console.error("Impossible de créer une feuille de temps: ID de l'opérateur manquant");
            }
            return;
        }

        // Si des détails existent, ouvrir la modal de détail
        if (data && data.details) {
            setModalOpen(true);
        }
    };

    const handleCreateEmpty = (operateurId, date, statutJournee, heureDebut, heureFin, tempsPauseMinutes) => {
        dispatch(heuresActions.createEmptyFeuilleDeTemps(operateurId, date, statutJournee, heureDebut, heureFin, tempsPauseMinutes))
            .then(data => {
                // Fermer la modal de création
                setCreateModalOpen(false);

                // Utiliser le callback pour rafraîchir les données au lieu de recharger la page
                if (onRefresh && typeof onRefresh === 'function') {
                    onRefresh();
                }
            })
            .catch(error => {
                console.error("Failed to create empty feuille de temps:", error);
            });
    };

    // Validation de l'intervalle des dates
    if (
        !dateRange.startDate ||
        !dateRange.endDate ||
        !isValid(new Date(dateRange.startDate)) ||
        !isValid(new Date(dateRange.endDate)) ||
        new Date(dateRange.startDate) > new Date(dateRange.endDate)
    ) {
        console.error("Intervalle invalide : ", dateRange);
        return <div>Erreur : Intervalle invalide.</div>;
    }

    // Jours de l'intervalle
    const allDays = eachDayOfInterval({
        start: new Date(dateRange.startDate),
        end: new Date(dateRange.endDate),
    });

    // Transformation des données pour les `rows` (avant filtrage)
    const allRows = heures?.map((item, index) => {
            const totalMinutes = typeof item.totalMinutes === 'number' ? item.totalMinutes : 0;
            const totalHours = (totalMinutes / 60).toFixed(2);

            const overtimeMinutes = typeof item.overtimeMinutes === 'number' ? item.overtimeMinutes : 0;
            const overtimeHours = (overtimeMinutes / 60).toFixed(2);

        return {
            id: index,
            operateur: item.operateur,
            totalHours,
            overtimeHours,
            name: ` ${item.operateur} (${totalHours}H)`,
            "name-mirror": ` ${item.operateur} (${totalHours}H)`,
            ...item.suivi.reduce((acc, day) => {
                acc[day.date] = day;
                return acc;
            }, {}),
        };
    });

    // Filtrage : jours et lignes ayant au moins une case à valider (status vert/orange/rouge avec details mais sans validatedAt)
    const isToValidate = (cellData) => {
        if (!cellData || !cellData.details) return false;
        if (cellData.status === 'gris' || cellData.status === 'orange') return false;
        return !cellData.details.validatedAt;
    };

    // Recherche opérateur d'abord, puis filtre "à valider" : les colonnes affichées
    // se limitent ainsi aux jours à valider des opérateurs recherchés
    const searchedRows = searchOperateur.trim()
        ? allRows?.filter(row => normalizeText(row.operateur).includes(normalizeText(searchOperateur)))
        : allRows;

    const filteredDays = filterNonValidated
        ? allDays.filter(date => {
            const field = format(date, "yyyy-MM-dd");
            return searchedRows?.some(row => isToValidate(row[field]));
          })
        : allDays;

    const filteredRows = filterNonValidated
        ? searchedRows?.filter(row =>
            filteredDays.some(date => isToValidate(row[format(date, "yyyy-MM-dd")]))
          )
        : searchedRows;

    // Génération des colonnes pour chaque jour du mois
    const columns = [
        {
            field: "name",
            headerName: "Opérateur",
            width: 200,
            renderCell: (params) => {
                const operateur = params.row?.operateur || (typeof params.value === 'string' ? params.value.split(' (')[0] : '');
                const totalHours = params.row?.totalHours;
                const overtimeHours = params.row?.overtimeHours;
                return (
                    <div style={{display: 'flex', flexDirection: 'row', paddingLeft: '1rem', paddingRight: '1rem', justifyContent: 'space-between'}}>
                        <div style={{ fontWeight: 400 }}>{operateur}</div>
                        <div style={{ fontSize: '12px', color: '#555'}}>
                            { `${totalHours}H`}
                        </div>
                    </div>
                );
            },
            headerClassName: "operator-header",
        },
        {
            field: "overtimeSummary",
            headerName: "HS",
            width: 80,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const overtimeHours = params.row?.overtimeHours;
                const value = Number(overtimeHours);
                if (isNaN(value)) return null;
                return (
                    <div style={{ fontSize: '12px', fontWeight: 500, color: value > 0 ? 'red' : '#555'}}>
                        {value > 0 ? `${overtimeHours}H` : ``}
                    </div>
                );
            },
        },
        ...filteredDays.map((date) => ({
            field: format(date, "yyyy-MM-dd"),
            headerName: `${format(date, "EEEE", {locale: fr})}\n${format(date, "d")}`,
            width: 60,
            renderCell: renderCellStyle,
        })),
        {
            field: "name-mirror",
            headerName: "Opérateur",
            width: 200,
            renderCell: (params) => {
                const operateur = params.row?.operateur || (typeof params.value === 'string' ? params.value.split(' (')[0] : '');
                const totalHours = params.row?.totalHours;
                const overtimeHours = params.row?.overtimeHours;
                return (
									<div style={{display: 'flex', flexDirection: 'row', paddingLeft: '1rem'}}>
										<div style={{ fontWeight: 400 }}>{operateur}</div>
										<div style={{ fontSize: '12px', color: '#555', paddingLeft: '1rem' }}>
											{ `${totalHours}H`}
											{Number(overtimeHours) > 0 ? (
												<span style={{ color: 'red', marginLeft: 6 }}>+{overtimeHours}H</span>
											) : null}
										</div>
									</div>
                );
            },
            headerClassName: "operator-header",
        },
    ];


    // Rendu personnalisé pour les cellules en fonction du statut
    function renderCellStyle(params) {
        const data = params.value; // `params.value` contient tout l'objet d'un jour
        if (!data) return null;

        const {status, details} = data;
        const workMinutes = details?.workMinutes || 0;
        const validatedAt = details?.validatedAt

        // 100% (et non 60px fixe) : la hauteur réelle est celle de la ligne du DataGrid
        // (rowHeight) — un contenu plus haut que la ligne serait rogné en bas, ce qui
        // coupait les icônes positionnées en bottom.
        const cellSize = {width: '100%', height: '100%', position: 'relative'}

        let cellStyle;
        let label;

        // Si statutJournee est défini et différent de "WD", on prend les infos de l'enum
        if (details.statutJournee && details.statutJournee !== "WD") {
            // Mapper RTT vers Repos (l'API peut encore renvoyer RTT)
            const statutId = details.statutJournee === "RTT" ? "Repos" : details.statutJournee;
            // Chercher l'option correspondante dans vos constantes
            const option = Constants.RH_STATUT_JOURNEE_DATA.find(opt => opt.id === statutId);
            if (option) {
                cellStyle = {
                    backgroundColor: option.color,
                    color: option.fontColor,
                    ...cellSize,
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                };
                // JFT : pas de libellé dans la cellule (trop serré), la couleur corail + le
                // tooltip suffisent, et les heures réelles s'affichent comme pour un jour travaillé
                label = statutId === "JFT" ? "" : option.id;
            } else {
                cellStyle = {backgroundColor: "lime", color: "black", ...cellSize};
                label = details.statutJournee;
            }
        } else {
            // Sinon, on se base sur le mapping hérité via "status"
            const styleMap = {
                vert: {backgroundColor: Constants.COLOR_THEME_GREEN, color: "black", ...cellSize},
                orange: {backgroundColor: Constants.COLOR_THEME_ORANGE, color: "black", ...cellSize},
                rouge: {backgroundColor: Constants.COLOR_THEME_RED, color: "white", ...cellSize},
                gris: {backgroundColor: "grey", color: "black", ...cellSize},
            };
            cellStyle = styleMap[status] || {backgroundColor: "grey", color: "black", ...cellSize};
            // Par défaut, afficher "Travail" pour WD ou laisser status
            label = ""
        }

        if (params.id === selectedRowId) {
            cellStyle.borderTop = "3px solid #03A9F4";
            cellStyle.borderBottom = "3px solid #03A9F4";
            cellStyle.height = "calc(100% - 6px)"; // compense les 2 bordures pour ne pas rogner le bas
        }

        const validatedBadge = validatedAt ? (
            <div style={{
                position: 'absolute', top: 2, left: 2,
                fontSize: 11, lineHeight: 1, color: '#1565C0',
                pointerEvents: 'none', fontWeight: 'bold'
            }} title="Validé">✔</div>
        ) : null;

        const alerte = data?.alerte || null;
        const alerteTypes = alerte?.types || [];
        const hasIdle = alerteTypes.includes('idle_prolonge');
        const hasOther = alerteTypes.some((t) => t !== 'idle_prolonge');
        let alerteBadge = null;

        if (alerte && hasOther) {
            alerteBadge = (
              <div style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 10, height: 10, borderRadius: '50%',
                  backgroundColor: '#d32f2f', pointerEvents: 'none'
              }} title={`${alerte?.count} alerte(s) RH`} />
            );

           /* if (alerte.maxSeverity === 'critical') {
                alerteBadge = (
                    <div style={{
                        position: 'absolute', top: 2, right: 2,
                        width: 10, height: 10, borderRadius: '50%',
                        backgroundColor: '#d32f2f', pointerEvents: 'none'
                    }} title={`${alerte.count} alerte(s) critique(s)`} />
                );
            } else if (alerte.maxSeverity === 'warning') {
                alerteBadge = (
                    <div style={{
                        position: 'absolute', top: 0, right: 1,
                        fontSize: 14, lineHeight: 1, color: '#e65100',
                        pointerEvents: 'none', fontWeight: 'bold'
                    }} title={`${alerte.count} alerte(s)`}>⚠</div>
                );
            }*/
        }

        const idleBadge = hasIdle ? (
            <LocalShippingIcon
                titleAccess="Arrêt moteur prolongé pendant le poste"
                style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 15, color: '#d32f2f', pointerEvents: 'none' }}
            />
        ) : null;

        // Déplacement (forfait hebdo) : posé sur la semaine, affiché sur chacun de ses jours
        const deplacementBadge = data?.deplacement ? (
            <FlightTakeoff
                titleAccess="Déplacement (forfait semaine)"
                style={{ position: 'absolute', bottom: 2, right: 2, fontSize: 14, color: '#6A1B9A', opacity: 0.9, pointerEvents: 'none' }}
            />
        ) : null;

        return (
            <div
                style={cellStyle}
                onClick={() => handleCellClick(params)}
                title={details.statutJournee === "JFT" ? "Jour férié travaillé (forfait 7h + heures réelles)" : undefined}
            >
                {/* Panier repas icon in top-right if applicable */}
                {(data?.panierRepas || details?.panierRepas) && (
                    <PanierRepasIcon style={{ position: 'absolute', top: 2, right: 2, fontSize: 14, opacity: 0.9, pointerEvents: 'none' }} />
                )}
                {validatedBadge}
                {alerteBadge}
                {idleBadge}
                {deplacementBadge}
                <div>{label}</div>
                {/* Affichage complémentaire pour les cas "travail" et "jour férié travaillé" (heures réelles) */}
                {(details.statutJournee === "WD" || details.statutJournee === "JFT") && (
                    <>
                        <div style={{width: "100%", textAlign: "center"}}>
                            {/*{Math.floor(workMinutes / 60)}h{workMinutes % 60 > 0 ? workMinutes % 60 + "m" : ""}*/}
                            {(workMinutes / 60).toFixed(2)}h
                        </div>
                    </>
                )}
            </div>
        );
    }

    const updateWorkMinutesLocally = (id, heureDebut, heureFin, tempsPauseMinutes) => {
        const start = new Date(heureDebut);
        const end = new Date(heureFin);

        const workMinutes = (end - start) / (1000 * 60) - tempsPauseMinutes; // Différence en minutes

        // Mise à jour de la données qui est passée à la modal
        setSelectedData((prevData) => ({
            ...prevData,
            details: {
                ...prevData.details,
                debut: heureDebut,
                fin: heureFin,
                tempsPauseMinutes: tempsPauseMinutes,
                workMinutes,
            },
        }));

        // Mise à jour du tableau
        heures.forEach((row) => {
            row.suivi.forEach((day) => {
                if (day.id === id) {
                    day.details.workMinutes = workMinutes;
                    day.details.debut.date = heureDebut;
                    day.details.fin.date = heureFin;
                    day.details.tempsPauseMinutes = tempsPauseMinutes
                }
            });
        });
    };

    const onFdtUpdate = (values) => {
        if (values.id) {
            dispatch(heuresActions.updateFeuilleDeTemps(values.id, values))
                .then(data => {
                    if (data.heureDebut && data.heureFin && data.tempsPauseMinutes) {
                        updateWorkMinutesLocally(values.id, data.heureDebut, data.heureFin, data.tempsPauseMinutes);
                    }
                })
                .catch(error => {
                    console.error("Failed to update feuille de temps:", error);
                });
        }
    }

    const onFdtValidate = (fdtIds = []) => {
        const now = new Date().toISOString();

        Promise.all(
            fdtIds.map(id => {
                const values = { id, validatedAt: now };
                return dispatch(heuresActions.updateFeuilleDeTemps(id, values));
            })
        ).then(() => {
            setModalOpen(false);
        }).catch(error => {
            console.error("Erreur lors de la validation : ", error);
        });
    };


    return (
        <Grid container size={12} spacing={2}>
          {/*  <Grid size={12}>
                <Typography variant="h6">Suivi des heures</Typography>
            </Grid>*/}
            <Grid size={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    size="small"
                    placeholder="Rechercher un opérateur"
                    value={searchOperateur}
                    onChange={(e) => setSearchOperateur(e.target.value)}
                    sx={{ width: 260 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControlLabel
                  control={
                      <Switch
                        checked={filterNonValidated}
                        onChange={(e) => setFilterNonValidated(e.target.checked)}
                        size="small"
                      />
                  }
                  label="À valider uniquement"
                  labelPlacement="start"
                />
            </Grid>
            <Grid size={12}>
                <div style={{height: "100%", width: "100%"}}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        rowHeight={60}
                        pageSize={10}
                        rowsPerPageOptions={[5, 10, 20]}
                        disableSelectionOnClick
                        disableColumnMenu
                        disableColumnFilter
                        disableColumnSorting
                        onCellClick={(params) => {
                            // Mettre en surbrillance la ligne pour n'importe quelle cellule
                            setSelectedRowId((prev) => (prev === params.id ? null : params.id));

                            // Si ce n'est pas une cellule opérateur, traiter le clic normalement
                            if (params.field !== "name" && params.field !== "name-mirror") {
                                handleCellClick(params);
                            }
                        }}
                        getRowClassName={(params) => {
                            return params.id === selectedRowId ? 'Mui-selected-row' : '';
                        }}
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        sx={{
                            "& .MuiDataGrid-columnHeaderTitle": {
                                whiteSpace: "pre-wrap", // Autorise les sauts de ligne
                                textAlign: "center",   // Centre le texte
                                lineHeight: "1.2",    // Ajuste la hauteur des lignes
                            },
                            "& .MuiDataGrid-cell": {
                                padding: 0,
                                border: '1px solid lightgrey',
                                cursor: 'pointer'
                            },
                            "& .MuiDataGrid-cell--textLeft:first-of-type": {
                                position: "sticky",
                                left: 0,
                                backgroundColor: "#fff",
                                zIndex: 2,
                            },
                            "& .MuiDataGrid-columnHeader--textLeft:first-of-type": {
                                position: "sticky",
                                left: 0,
                                backgroundColor: "#f5f5f5",
                                zIndex: 3,
                            },
                            "& .Mui-selected-row": {
                                backgroundColor: "#03A9F4 !important",
                            },
                        }}
                    />
                </div>
            </Grid>

            <EquipageDetailModal
                posteOperateurId={selectedData?.details?.posteOperateurId}
                open={modalOpen} 
                onFdtUpdate={onFdtUpdate}
                handleClose={() => setModalOpen(false)}
                onFdtValidate={onFdtValidate}
                onRefresh={onRefresh}
                initialStatutJournee={selectedData?.details?.statutJournee}
                onStatutJourneeChange={(newStatutJournee) => {
                    // Update the selectedData with the new statutJournee
                    setSelectedData(prevData => ({
                        ...prevData,
                        details: {
                            ...prevData.details,
                            statutJournee: newStatutJournee
                        }
                    }));
                }}
            />

            {/* Modal pour créer une feuille de temps vide */}
            <FdtCreateModal
                open={createModalOpen}
                handleClose={() => setCreateModalOpen(false)}
                data={selectedData}
                onCreateEmpty={handleCreateEmpty}
            />

            {/*/!* Modal pour afficher les détails *!/*/}
            {/*<DetailHeureModal*/}
            {/*    open={modalOpen} handleClose={() => setModalOpen(false)}*/}
            {/*    data={selectedData} quartixData={quartixData}*/}
            {/*    loading={loading} onFdtUpdate={onFdtUpdate}*/}
            {/*    onFdtValidate={onFdtValidate}/>*/}
        </Grid>
    );
}

const mapStateToProps = (state) => ({
    loading: state.heures.loading,
    error: state.heures.error,
});

export default connect(mapStateToProps)(RHSuiviHeuresTable);
